import { CustomTooltip, NormalButton, ViewMoreButton } from '@/shared/ui/components';
import { chargeIcon, configIcon } from '@/shared/ui/assets';
import { DEVICE, DEVICE_TABLE_TYPE, DOUBLE_HYPHEN_BULLET } from '@/shared/config';
import { isFacilitySubscribeProtectPlus } from '@/entities/facility/@x/device';
import {
  formatEndOfLife,
  zeroPad,
  convertDeviceModel,
  dateToDuration,
  dateToString,
  dateToTimeSince,
  gettextDeviceModel,
} from '@/shared/lib';
import dayjs from 'dayjs';
import _ from 'lodash';
import Skeleton from 'react-loading-skeleton';
import { t } from 'ttag';
import { isEndOfLife } from './isEndOfLife';
import { checkUsingHighSamplingRateDevice } from './checkUsingHighSamplingRateDevice';
import NetworkModeFormatter from '../ui/networkModeFormatter';
import DeviceStatusFormatter from '../ui/deviceStatusFormatter';
import DeviceIdFormatter from '../ui/deviceIdFormatter/deviceIdFormatter';

/**
 * Flattens studies awaiting upload into device table rows. Maps each study to its device's additional status, device id, last sync and model together with the study id, the friendly id as last study id, facility, device type, first study date, a duration since the last sync, and an action button tag built from the device id and table type.
 *
 * @param studies - Studies whose devices are awaiting upload.
 * @param type - Device table type deciding the action button tag suffix.
 * @returns The device rows for the awaiting-upload table.
 */
const awaitingUploadDevicesFormatter = ({ studies, type }: any) =>
  _.map(studies, (study) => {
    const { device } = study;
    return {
      additionalStatus: device.additionalStatus || '',
      studyId: study.id,
      actionButtonTag: `${device.deviceId}-${type === DEVICE_TABLE_TYPE.ALL ? 'all' : 'in-study'}`,
      deviceId: study.deviceId || '',
      lastSync: device.lastSync,
      lastConnect: dateToDuration(device?.lastSync?.time),
      facility: study.facility,
      lastStudyId: study.friendlyId,
      deviceModel: device.model,
      deviceType: study.deviceType,
      firstStudyDate: device?.firstStudyDate,
    };
  });

/**
 * Flattens devices tied to RMA tickets into table rows. Maps each device to its id, additional status, renewal flag, model, MCT capability, facility and last sync, the last used study's friendly and internal ids, a duration since the last sync, the end-of-life lifetime from the first study date, and an action button tag from the device id and table type.
 *
 * @param devices - RMA device records to convert into rows.
 * @param type - Device table type deciding the action button tag suffix.
 * @returns The device rows for the RMA table.
 */
const rmaDevicesFormatter = ({ devices, type }: any) =>
  _.map(devices, (device) => {
    const { lastUsed } = device;
    return {
      deviceId: device.deviceId || '',
      additionalStatus: device.additionalStatus || '',
      isRenewal: device?.isRenewal,
      firstStudyDate: device?.firstStudyDate,
      lifetime: device?.firstStudyDate ? formatEndOfLife(device.firstStudyDate) : '',
      lastSync: device.lastSync,
      deviceModel: device.model,
      canRunBiotresMCT: device.canRunBiotresMCT,
      lastStudyId: lastUsed?.study?.friendlyId,
      studyId: lastUsed?.study?.id,
      lastConnect: dateToDuration(device?.lastSync?.time),
      facility: device.facility,
      actionButtonTag: `${device.deviceId}-${type === DEVICE_TABLE_TYPE.ALL ? 'all' : 'in-study'}`,
    };
  });

/**
 * Flattens devices needing maintenance into table rows. Maps each device to its id, status, model, facility and last sync, the last used study's friendly and internal ids, a duration since the last sync, the end-of-life lifetime from the first study date, the running-study, renewal and Biotres MCT flags, the facility programs, and an in-study action button tag.
 *
 * @param devices - Maintenance device records to convert into rows.
 * @returns The device rows for the maintenance table.
 */
const maintenanceDevicesFormatter = ({ devices }: any) =>
  _.map(devices, (device) => {
    const { lastUsed } = device;
    return {
      additionalStatus: device.additionalStatus || '',
      actionButtonTag: `${device.deviceId}-in-study`,
      deviceId: device.deviceId || '',
      status: device.status,
      lastSync: device.lastSync,
      lastConnect: dateToDuration(device?.lastSync?.time),
      facility: device.facility,
      lastStudyId: lastUsed?.study?.friendlyId,
      lastUsed: lastUsed?.time,
      studyId: lastUsed?.study?.id,
      deviceModel: device.model,
      lifetime: device?.firstStudyDate ? formatEndOfLife(device.firstStudyDate) : '',
      firstStudyDate: device?.firstStudyDate,
      isRunningStudy: device?.isRunningStudy,
      isRenewal: device?.isRenewal,
      canRunBiotresMCT: device.canRunBiotresMCT,
      programs: device.facility?.programs,
    };
  });

/**
 * Flattens device records into devices table rows. Maps each device to its id, status, model, facility and last sync, plus the last used study's friendly and internal ids, a duration since the last sync, the end-of-life lifetime derived from the first study date, the renewal and Biotres MCT flags, the facility programs, and an action button tag from the device id and table type.
 *
 * @param devices - Device records to convert into rows.
 * @param type - Device table type deciding the action button tag suffix.
 * @returns The device rows for the devices table.
 */
const devicesFormatter = ({ devices, type }: any) =>
  _.map(devices, (device) => {
    const { lastUsed } = device;
    return {
      additionalStatus: device.additionalStatus || '',
      actionButtonTag: `${device.deviceId}-${type === DEVICE_TABLE_TYPE.ALL ? 'all' : 'in-study'}`,
      deviceId: device.deviceId || '',
      status: device.status,
      lastSync: device.lastSync,
      lastConnect: dateToDuration(device?.lastSync?.time),
      facility: device.facility,
      lastStudyId: lastUsed?.study?.friendlyId,
      lastUsed: lastUsed?.time,
      studyId: lastUsed?.study?.id,
      deviceModel: device.model,
      lifetime: device?.firstStudyDate ? formatEndOfLife(device.firstStudyDate) : '',
      firstStudyDate: device?.firstStudyDate,
      isRenewal: device?.isRenewal,
      canRunBiotresMCT: device.canRunBiotresMCT,
      programs: device.facility?.programs,
    };
  });

/**
 * Renders the last study id column of the devices table. Shows a zero-padded text button that opens the study info drawer for the row's study when a value exists, a double-hyphen placeholder when it does not, and a skeleton while data loads.
 *
 * @param cell - Last study friendly id of the row.
 * @param row - Full device row.
 * @param rowIndex - Index of the row in the table.
 * @param formatExtraData - Extra table data with the loaded flag and the study drawer opener.
 * @returns The last study id cell element or a skeleton.
 */
const deviceLastStudyIdFormatter = (cell: any, row: any, rowIndex: any, formatExtraData: any) => {
  const { isLoadedData, drawerStudyInfo } = formatExtraData;
  if (isLoadedData) {
    return cell ? (
      <NormalButton
        id="device-last-study-id-btn"
        isText
        buttonName={zeroPad(cell)}
        onClick={() => {
          drawerStudyInfo(row.studyId);
        }}
      />
    ) : (
      DOUBLE_HYPHEN_BULLET
    );
  }
  return <Skeleton count={1} />;
};

/**
 * Renders the row action icon of the devices table. Once the data has loaded it shows a view-more button with a single config icon whose tooltip reads Initiate Protect+ program or Initiate RMA request depending on whether the row's facility subscribes to Protect+, calling the initiate handler with the device id, model and facility, and nothing before that.
 *
 * @param cell - Unused cell value of the row.
 * @param row - Full device row supplying the device id, model and facility.
 * @param rowIndex - Index of the row in the table.
 * @param formatExtraData - Extra table data with the loaded flag and the initiate Protect+ handler.
 * @returns The action icon element, or undefined while data loads.
 */
const actionIconFormatter = (cell: any, row: any, rowIndex: any, formatExtraData: any) => {
  const { isLoadedData, handleClickInitiateProtectPlus } = formatExtraData;
  if (isLoadedData) {
    const isSubscribeProtectPlus = isFacilitySubscribeProtectPlus(row?.facility?.programs);
    return (
      <ViewMoreButton
        icons={
          [
            {
              id: `initiate-protect-plus-${row.actionButtonTag}`,
              tooltipTitle: isSubscribeProtectPlus
                ? t`Initiate Protect+ program`
                : t`Initiate RMA request`,
              icon: <img src={configIcon} alt="Initiate protect plus" />,
              onClick: () => {
                handleClickInitiateProtectPlus({
                  deviceId: row.deviceId,
                  model: row.deviceModel,
                  facility: row.facility,
                });
              },
            },
          ] as any
        }
      />
    );
  }
  return undefined;
};

/**
 * Renders the last used date column of the devices table. Formats the cell as MM/DD/YYYY in UTC when set, falls back to the double-hyphen placeholder, and shows a skeleton while data loads.
 *
 * @param cell - Last used timestamp of the row.
 * @param row - Full device row.
 * @param rowIndex - Index of the row in the table.
 * @param formatExtraData - Extra table data carrying the loaded flag.
 * @returns The last used cell element or a skeleton.
 */
const deviceLastUsedFormatter = (cell: any, row: any, rowIndex: any, formatExtraData: any) => {
  const { isLoadedData } = formatExtraData;
  if (isLoadedData) {
    return cell
      ? dateToString({
          isoDate: cell,
          timezone: 'UTC+00',
          format: 'MM/DD/YYYY',
        })
      : DOUBLE_HYPHEN_BULLET;
  }
  return <Skeleton count={1} />;
};

/**
 * Renders the device id column of the devices table together with its state tags. Builds a device id cell that opens the info drawer for the row and flags RMA, renewal, running study, end of life and high sampling rate from the row's additional status, sync schema and first study date, or a skeleton while data loads.
 *
 * @param cell - Device id of the row.
 * @param row - Full device row.
 * @param rowIndex - Index of the row in the table.
 * @param formatExtraData - Extra table data with the loaded flag, drawer toggle and renewal-tag switch.
 * @returns The device id cell element or a skeleton.
 */
const deviceIdFormatter = (cell: any, row: any, rowIndex: any, formatExtraData: any) => {
  const { isLoadedData } = formatExtraData;
  if (isLoadedData) {
    const { toggleDisplayDrawer, isShowRenewTag } = formatExtraData;
    return (
      <DeviceIdFormatter
        deviceId={cell}
        handleClickDeviceId={() => {
          toggleDisplayDrawer(row);
        }}
        rma={row.additionalStatus === 'RMA'}
        isRenewal={isShowRenewTag && row.isRenewal}
        isRunningStudy={row.isRunningStudy}
        isEOL={isEndOfLife(row?.firstStudyDate)}
        isHighSamplingRate={checkUsingHighSamplingRateDevice(row?.lastSync?.schema)}
      />
    );
  }
  return <Skeleton count={1} />;
};

/**
 * Renders a device model label, adding an auto-event-detection badge where it applies. Converts and translates the model name, and appends a tooltipped Auto tag when the row's facility is not a test facility, the device can run Biotres MCT and it is not a Biotres Pro.
 *
 * @param cell - Raw device model of the row.
 * @param row - Full device row supplying the facility and MCT capability.
 * @returns The model label element, with the Auto tag when applicable.
 */
const getBadge = (cell: any, row: any) => {
  const isBiotresPro = row.model === DEVICE.BIOTRES_PRO;
  const shouldDisplayAutoTag =
    row.facility?.status !== 'Test' && row.canRunBiotresMCT && !isBiotresPro;
  const deviceModel = convertDeviceModel(cell);
  if (shouldDisplayAutoTag) {
    return (
      <div className="flex items-center no-wrap flex-wrap">
        <span>{deviceModel ? gettextDeviceModel(deviceModel) : DOUBLE_HYPHEN_BULLET}</span>
        <CustomTooltip title={t`Auto-event detection enabled`} arrow={false} placement="bottom">
          <span className="auto-event">{t`Auto`}</span>
        </CustomTooltip>
      </div>
    );
  }

  return <span>{deviceModel ? gettextDeviceModel(deviceModel) : DOUBLE_HYPHEN_BULLET}</span>;
};

/**
 * Renders the model column of the devices table. Delegates to the model badge builder once the data has loaded, which adds the auto-event-detection tag where it applies, and shows a skeleton before that.
 *
 * @param cell - Raw device model of the row.
 * @param row - Full device row.
 * @param rowIndex - Index of the row in the table.
 * @param formatExtraData - Extra table data carrying the loaded flag.
 * @returns The model cell element or a skeleton.
 */
const modelFormatter = (cell: any, row: any, rowIndex: any, formatExtraData: any) => {
  const { isLoadedData } = formatExtraData;
  if (isLoadedData) {
    return getBadge(cell, row);
  }
  return <Skeleton count={1} />;
};

/**
 * Renders the status column of the devices table. Once the data has loaded it shows the device status formatter with the status circle enabled, passing the row's device id and the event notifications from the extra data, and before that a skeleton.
 *
 * @param cell - Device status of the row.
 * @param row - Full device row supplying the device id.
 * @param rowIndex - Index of the row in the table.
 * @param formatExtraData - Extra table data with the loaded flag and the event notifications.
 * @returns The status cell element or a skeleton.
 */
const statusFormatter = (cell: any, row: any, rowIndex: any, formatExtraData: any) => {
  const { isLoadedData } = formatExtraData;
  if (isLoadedData) {
    const { eventsNoti } = formatExtraData;
    return (
      <DeviceStatusFormatter
        deviceId={row.deviceId}
        {...({ isShowStatusCircle: true } as any)}
        status={cell}
        eventsNoti={eventsNoti}
      />
    );
  }
  return <Skeleton count={1} />;
};

/**
 * Renders the firmware version column of the devices table. Shows the cell or the double-hyphen placeholder once the table data has loaded, and a skeleton before that.
 *
 * @param cell - Firmware version of the row.
 * @param row - Full device row.
 * @param rowIndex - Index of the row in the table.
 * @param formatExtraData - Extra table data carrying the loaded flag.
 * @returns The firmware version cell element or a skeleton.
 */
const fwVersionFormatter = (cell: any, row: any, rowIndex: any, formatExtraData: any) => {
  const { isLoadedData } = formatExtraData;
  if (isLoadedData) {
    return <span>{cell || DOUBLE_HYPHEN_BULLET}</span>;
  }
  return <Skeleton count={1} />;
};

/**
 * Renders the facility column of the devices table. Wraps the cell value in a span once the table data has loaded and shows a skeleton before that.
 *
 * @param cell - Facility name of the row.
 * @param row - Full device row.
 * @param rowIndex - Index of the row in the table.
 * @param formatExtraData - Extra table data carrying the loaded flag.
 * @returns The facility cell element or a skeleton.
 */
const facilityFormatter = (cell: any, row: any, rowIndex: any, formatExtraData: any) => {
  const { isLoadedData } = formatExtraData;
  if (isLoadedData) {
    return <span>{cell}</span>;
  }
  return <Skeleton count={1} />;
};

/**
 * Renders the last connect column of the devices table. Shows the cell as a time-since string when it is set and the double-hyphen placeholder otherwise, or a skeleton while data loads.
 *
 * @param cell - Last connect timestamp of the row.
 * @param row - Full device row.
 * @param rowIndex - Index of the row in the table.
 * @param formatExtraData - Extra table data carrying the loaded flag.
 * @returns The last connect cell element or a skeleton.
 */
const lastConnectFormatter = (cell: any, row: any, rowIndex: any, formatExtraData: any) => {
  const { isLoadedData } = formatExtraData;

  if (isLoadedData) {
    return <span>{cell ? dateToTimeSince(cell) : DOUBLE_HYPHEN_BULLET}</span>;
  }
  return <Skeleton count={1} />;
};

/**
 * Renders the operation status column of the devices table. Shows the cell or the double-hyphen placeholder once the table data has loaded, and a skeleton before that.
 *
 * @param cell - Operation status of the row.
 * @param row - Full device row.
 * @param rowIndex - Index of the row in the table.
 * @param formatExtraData - Extra table data carrying the loaded flag.
 * @returns The operation status cell element or a skeleton.
 */
const operationStatusFormatter = (cell: any, row: any, rowIndex: any, formatExtraData: any) => {
  const { isLoadedData } = formatExtraData;
  if (isLoadedData) {
    return <span>{cell || DOUBLE_HYPHEN_BULLET}</span>;
  }
  return <Skeleton count={1} />;
};

// *: There is rectangle with 3 slices of triangle (top-left, bottom-right and rssi value one)
/**
 * Renders the network mode column of the devices table. Shows the signal strength formatter fed by the cell and the row's last sync RSSI when a mode exists, the double-hyphen placeholder when not, and a skeleton while data loads.
 *
 * @param cell - Network mode of the row.
 * @param row - Full device row supplying the last sync RSSI.
 * @param rowIndex - Index of the row in the table.
 * @param formatExtraData - Extra table data carrying the loaded flag.
 * @returns The network mode cell element or a skeleton.
 */
const networkModeFormatter = (cell: any, row: any, rowIndex: any, formatExtraData: any) => {
  const { isLoadedData } = formatExtraData;
  if (isLoadedData) {
    if (cell) {
      return <NetworkModeFormatter networkMode={cell} rssi={row?.lastSync?.rssi} />;
    }
    return DOUBLE_HYPHEN_BULLET;
  }
  return <Skeleton count={1} />;
};

/**
 * Renders the charging column of the devices table. Shows a Charging label with the charge icon when the cell is truthy and Not charging otherwise, or a single skeleton row while the table data has not loaded.
 *
 * @param cell - Charging state of the row.
 * @param row - Full device row.
 * @param rowIndex - Index of the row in the table.
 * @param formatExtraData - Extra table data carrying the loaded flag.
 * @returns The charging cell element or a skeleton.
 */
const chargingStateFormatter = (cell: any, row: any, rowIndex: any, formatExtraData: any) => {
  const { isLoadedData } = formatExtraData;
  if (isLoadedData) {
    return (
      <span>
        {cell ? (
          <span>
            {t`Charging`} <img src={chargeIcon} alt="chargeIcon" />
          </span>
        ) : (
          t`Not charging`
        )}
      </span>
    );
  }
  return <Skeleton count={1} />;
};

/**
 * Turns an unused-period filter selection into the date range used to query devices. Maps the all and one-year presets to an open-ended range ending one or twelve months ago, otherwise locates the selection in the ordered month table and derives the from and to dates from the gap to the next entry, returning undefined for anything unrecognised.
 *
 * @param value - Selected unused-period filter option.
 * @returns The from and to dates for the selection, or undefined when it is unknown.
 */
const filterUnusedValue = (value: any) => {
  if (value === DEVICE_TABLE_TYPE.UNUSED_ALL) {
    return {
      toDate: dayjs().subtract(1, 'month').format('YYYY-MM-DD').concat('T00:00:00.000Z'),
      fromDate: undefined,
    };
  }

  if (value === DEVICE_TABLE_TYPE.UNUSED_ONEYEAR) {
    return {
      toDate: dayjs().subtract(12, 'month').format('YYYY-MM-DD').concat('T00:00:00.000Z'),
      fromDate: undefined,
    };
  }

  const arrMonthFilter = [
    {
      label: DEVICE_TABLE_TYPE.UNUSED_ONEMONTH,
      value: 1,
    },
    {
      label: DEVICE_TABLE_TYPE.UNUSED_TWOMONTH,
      value: 2,
    },
    {
      label: DEVICE_TABLE_TYPE.UNUSED_THREEMONTH,
      value: 3,
    },
    {
      label: DEVICE_TABLE_TYPE.UNUSED_SIXMONTH,
      value: 6,
    },
    {
      label: DEVICE_TABLE_TYPE.UNUSED_ONEYEAR,
      value: 12,
    },
  ];

  /**
   * Position of the selected unused-period option within the ordered month-offset table, matched by comparing each entry's label against the filter value. A value of -1 means the selection is not one of the discrete month options.
   *
   * @returns Index of the matching month-filter entry, or -1 when none matches.
   */
  const index = _.findIndex(arrMonthFilter, (data) => data.label === value);
  if (index > -1) {
    const toDate = dayjs().subtract(arrMonthFilter[index].value, 'months');
    return {
      fromDate: dayjs(toDate)
        .subtract(arrMonthFilter[index + 1].value - arrMonthFilter[index].value, 'months')
        .format('YYYY-MM-DD')
        .concat('T00:00:00.000Z'),
      toDate: toDate.format('YYYY-MM-DD').concat('T00:00:00.000Z'),
    };
  }

  return undefined;
};

export {
  actionIconFormatter,
  awaitingUploadDevicesFormatter,
  chargingStateFormatter,
  deviceIdFormatter,
  deviceLastStudyIdFormatter,
  deviceLastUsedFormatter,
  devicesFormatter,
  facilityFormatter,
  filterUnusedValue,
  fwVersionFormatter,
  lastConnectFormatter,
  maintenanceDevicesFormatter,
  modelFormatter,
  networkModeFormatter,
  operationStatusFormatter,
  rmaDevicesFormatter,
  statusFormatter,
};
